import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import dayjs from "dayjs";
import { getData } from "../../services/FetchNodeservices"; // Ensure this path is correct
import {
  FiSearch,
  FiDownload,
  FiChevronDown,
  FiX,
  FiFilter,
  FiColumns,
} from "react-icons/fi";

/**
 * UserPositionMatrix — FINAL VERSION
 * Single-row filters (Search + Multiselect + Status + Dates + Reset)
 */

export default function UserPositionMatrix() {
  const location = useLocation();
  const [data, setData] = useState([]);
  const [selected, setSelected] = useState([]);
  const [exportOpen, setExportOpen] = useState(false);

  // FILTER STATES
  const [search, setSearch] = useState("");

  const [companyFilter, setCompanyFilter] = useState([]);
  const [hrFilter, setHrFilter] = useState([]);
  const [positionFilter, setPositionFilter] = useState([]);
  const [associateFilter, setAssociateFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // DROPDOWN STATES
  const [openCompany, setOpenCompany] = useState(false);
  const [openHr, setOpenHr] = useState(false);
  const [openPosition, setOpenPosition] = useState(false);
  const [openAssociate, setOpenAssociate] = useState(false);
  const [openColumns, setOpenColumns] = useState(false);

  // COLUMN VISIBILITY
  const [columns, setColumns] = useState({
    company_name: true,
    hr_name: true,
    position: true,
    no_of_openings: true,
    location: true,
    budget: true,
    percentage: true,
    company_revenue: true,
    associate_name: true,
    profiles_shared: true,
    feedback_pending: true,
    screening: true,
    shortlisted: true,
    interview_l1: true,
    interview_l2: true,
    face_to_face: true,
    selected: true,
    offered: true,
    joining_pending: true,
    joined: true,
    rejected: true,
    no_show: true,
    hold: true,
    backout: true,
    duplicate: true,
    final_status: true,
  });

  // REFS FOR CLICK-OUTSIDE
  const companyRef = useRef();
  const hrRef = useRef();
  const positionRef = useRef();
  const associateRef = useRef();
  const columnsRef = useRef();
  const exportRef = useRef();

useEffect(() => {

  const fetchPositions = async () => {

    try {

      // 👇 logged in user
      const userData = JSON.parse(
        localStorage.getItem("USER")
      );

      const user_id = userData?.id;

      if (!user_id) {
        console.error("User ID not found");
        return;
      }

      const res = await getData(
        `positionMatrix/fetchJobData/${user_id}`
      );

      if (res.status) {

        setData(res.data || []);

      } else {

        console.error(
          "Failed to fetch position data:",
          res.message
        );

        setData([]);

      }

    } catch (error) {

      console.error(
        "Error fetching position data:",
        error
      );

      setData([]);

    }

  };

  fetchPositions();

}, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusParam = params.get("status");
    const dateParam = params.get("date");
    if (statusParam) {
      setStatusFilter(statusParam);
    }
    if (dateParam === "today") {
      const todayStr = dayjs().format("YYYY-MM-DD");
      setStartDate(todayStr);
      setEndDate(todayStr);
    }
  }, [location.search]);

  // UNIQUE LISTS FOR FILTERS
const companyList = useMemo(
  () => [...new Set((data || []).map((d) => d.company_name).filter(Boolean))],
  [data]
);
  const hrList = useMemo(
    () => [...new Set(data.map((d) => d.hr_name).filter(Boolean))],
    [data]
  );
  const positionList = useMemo(
    () => [...new Set(data.map((d) => d.position).filter(Boolean))],
    [data]
  );
  const associateList = useMemo(
    () => [...new Set(data.map((d) => d.associate_name).filter(Boolean))],
    [data]
  );
  const statusList = useMemo(
    () => [...new Set(data.map((d) => d.final_status).filter(Boolean))],
    [data]
  );

  /* ---------------- CLOSE DROPDOWNS WHEN CLICK OUTSIDE ---------------- */
  useEffect(() => {
    function handleClick(e) {
      if (companyRef.current && !companyRef.current.contains(e.target))
        setOpenCompany(false);
      if (hrRef.current && !hrRef.current.contains(e.target)) setOpenHr(false);
      if (positionRef.current && !positionRef.current.contains(e.target))
        setOpenPosition(false);
      if (associateRef.current && !associateRef.current.contains(e.target))
        setOpenAssociate(false);
      if (columnsRef.current && !columnsRef.current.contains(e.target))
        setOpenColumns(false);
      if (exportRef.current && !exportRef.current.contains(e.target))
        setExportOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  /* ---------------- FILTER LOGIC ---------------- */
  const filteredData = useMemo(() => {
    const q = search.trim().toLowerCase();

    return data.filter((row) => {
      if (companyFilter.length && !companyFilter.includes(row.company_name))
        return false;

      if (hrFilter.length && !hrFilter.includes(row.hr_name)) return false;

      if (positionFilter.length && !positionFilter.includes(row.position))
        return false;

      if (
        associateFilter.length &&
        !associateFilter.includes(row.associate_name)
      )
        return false;

      if (statusFilter && row.final_status !== statusFilter) return false;

      if (
        startDate &&
        row.position_received_on &&
        dayjs(row.position_received_on).isBefore(dayjs(startDate), "day")
      )
        return false;

      if (
        endDate &&
        row.position_received_on &&
        dayjs(row.position_received_on).isAfter(dayjs(endDate), "day")
      )
        return false;

      if (q) {
        // Search across multiple relevant string fields
        const matches = [
          row.company_name,
          row.hr_name,
          row.position,
          row.location,
          row.associate_name,
          row.final_status,
          String(row.budget || ''), // Include budget in search
          String(row.no_of_openings || ''), // Include openings in search
        ].some((v) => String(v || "").toLowerCase().includes(q));
        if (!matches) return false;
      }

      return true;
    });
  }, [
    data,
    search,
    companyFilter,
    hrFilter,
    positionFilter,
    associateFilter,
    statusFilter,
    startDate,
    endDate,
  ]);

  /* ---------------- MULTISELECT HANDLERS ---------------- */
  const toggleMulti = (val, setter) =>
    setter((prev) =>
      prev.includes(val) ? prev.filter((x) => x !== val) : [...prev, val]
    );

  const selectAll = (list, setter) => setter([...list]);
  const clearAll = (setter) => setter([]);

  /* ---------------- STATUS BADGES ---------------- */
  const statusBadge = (s) => {
    if (!s) return "bg-gray-200 text-gray-700";
    s = s.toLowerCase();
    if (s.includes("active") || s.includes("joined"))
      return "bg-green-100 text-green-700";
    if (s.includes("hold") || s.includes("pending"))
      return "bg-yellow-100 text-yellow-700";
    if (s.includes("reject") || s.includes("closed") || s.includes("backout") || s.includes("no show"))
      return "bg-red-100 text-red-700";
    if (s.includes("offered") || s.includes("selected"))
      return "bg-blue-100 text-blue-700";
    return "bg-gray-200 text-gray-800";
  };

  /* ---------------- RESET FILTERS ---------------- */
  const resetFilters = () => {
    setSearch("");
    setCompanyFilter([]);
    setHrFilter([]);
    setPositionFilter([]);
    setAssociateFilter([]);
    setStatusFilter("");
    setStartDate("");
    setEndDate("");
  };

  /* ---------------- EXPORT ---------------- */
  const handleExport = (type) => {
    const rows = selected.length
      ? filteredData.filter((r) => selected.includes(r.job_id))
      : filteredData;

    if (!rows.length) return alert("No records to export.");

    const exportColumns = Object.keys(columns).filter((colKey) => columns[colKey]);

    const headers = [
      "SR",
      "Received On",
      ...exportColumns.map((colKey) => columnHeaders[colKey] || colKey),
    ];

    const rowsData = rows.map((r, i) => {
      const rowArr = [
        i + 1,
        r.position_received_on
          ? dayjs(r.position_received_on).format("DD-MM-YYYY")
          : "",
      ];

      exportColumns.forEach((colKey) => {
        rowArr.push(r[colKey] ?? "");
      });

      return rowArr;
    });

    if (type === "excel" || type === "csv") {
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rowsData]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Matrix");
      XLSX.writeFile(wb, `position_matrix.${type === "csv" ? "csv" : "xlsx"}`);
    }

    if (type === "pdf") {
      const doc = new jsPDF("landscape");
      doc.text("Position Matrix Report", 10, 10);
      doc.autoTable({ head: [headers], body: rowsData });
      doc.save("position_matrix.pdf");
    }

    setExportOpen(false);
  };

  /* ---------------- SELECT ROWS ---------------- */
  const toggleSelectAll = (e) =>
    setSelected(e.target.checked ? filteredData.map((r) => r.job_id) : []);

  const toggleSelect = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  /* Helper for display column names */
  const columnHeaders = {
    company_name: "Company Name",
    hr_name: "HR Name",
    position: "Position",
    no_of_openings: "No. of Openings",
    location: "Location",
    budget: "Budget CTC",
    percentage: "Vendor %",
    company_revenue: "Company Revenue",
    associate_name: "Associate Name",
    profiles_shared: "Profiles Shared",
    feedback_pending: "Feedback Pending",
    screening: "Screening",
    shortlisted: "Shortlisted",
    interview_l1: "Interview L1",
    interview_l2: "Interview L2",
    face_to_face: "Face to Face",
    selected: "Selected",
    offered: "Offered",
    joining_pending: "Joining Pending",
    joined: "Joined",
    rejected: "Rejected",
    no_show: "No Show",
    hold: "Hold",
    backout: "Backout",
    duplicate: "Duplicate",
    final_status: "Final Status",
  };

  /* ======================================================================
                    🎯  UI STARTS BELOW — COMPLETE FINAL UI
  ====================================================================== */

  return (
    <div className="w-full space-y-4">

      {/* TOP HEADER */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-extrabold text-gray-800 uppercase tracking-wider">
            Position Matrix
          </h2>
          <div className="mt-1 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider">
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">
              Total Positions: {filteredData.length}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* EXPORT BUTTON */}
          <div className="relative" ref={exportRef}>
            <button
              onClick={() => setExportOpen((p) => !p)}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-lg shadow-indigo-100 transition duration-200 active:scale-[0.99] flex items-center gap-2"
            >
              <FiDownload /> Export <FiChevronDown />
            </button>

            {exportOpen && (
              <div className="absolute right-0 mt-2 bg-white border border-gray-100 rounded-2xl shadow-xl w-40 z-50 animate-fadeIn overflow-hidden">
                <button
                  className="block w-full px-4 py-2.5 hover:bg-gray-50 text-left transition text-xs font-bold text-gray-700"
                  onClick={() => handleExport("excel")}
                >
                  Excel
                </button>
                <button
                  className="block w-full px-4 py-2.5 hover:bg-gray-50 text-left transition text-xs font-bold text-gray-700"
                  onClick={() => handleExport("csv")}
                >
                  CSV
                </button>
                <button
                  className="block w-full px-4 py-2.5 hover:bg-gray-50 text-left transition text-xs font-bold text-gray-700"
                  onClick={() => handleExport("pdf")}
                >
                  PDF
                </button>
              </div>
            )}
          </div>

          {/* COLUMN CUSTOMIZER */}
          <div className="relative" ref={columnsRef}>
            <button
              onClick={() => setOpenColumns((p) => !p)}
              className="px-4 py-2 bg-white border border-gray-200 text-xs font-bold text-gray-700 rounded-xl shadow-sm hover:bg-gray-50 transition duration-200 active:scale-[0.99] flex items-center gap-2"
            >
              <FiColumns /> Columns
            </button>

            {openColumns && (
              <div className="absolute right-0 mt-2 bg-white border border-gray-100 rounded-2xl shadow-xl w-64 p-4 max-h-72 overflow-y-auto z-50 animate-fadeIn">
                <strong className="text-xs font-extrabold text-gray-800 uppercase tracking-wider block mb-2">
                  Show / Hide Columns
                </strong>
                <div className="grid grid-cols-2 gap-2 text-gray-700">
                  {Object.keys(columns).map((col) => (
                    <label
                      key={col}
                      className="flex items-center text-xs gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={columns[col]}
                        onChange={() =>
                          setColumns((prev) => ({
                            ...prev,
                            [col]: !prev[col],
                          }))
                        }
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="truncate">{columnHeaders[col] || col.replace(/_/g, " ")}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>


      {/* ======================================================================
                      🔥 SINGLE-ROW FILTER BAR (Search + Filters)
      ====================================================================== */}

      <div className="border border-gray-100 rounded-2xl overflow-visible bg-white shadow-sm">
        <div className="border-b border-gray-100 bg-gray-50/20 p-4 flex flex-wrap items-center gap-3 relative z-30">
          {/* SEARCH */}
          <div className="flex items-center bg-white border border-gray-200 rounded-xl px-3 h-10 flex-grow max-w-sm">
            <FiSearch className="text-gray-400 mr-2 w-4 h-4" />
            <input
              type="text"
              placeholder="Search all fields..."
              className="outline-none text-xs w-full text-gray-700 bg-transparent"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                className="ml-2 text-gray-455 hover:text-gray-600 transition"
                onClick={() => setSearch("")}
              >
                <FiX className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* COMPANY */}
          <div className="relative" ref={companyRef}>
            <button
              onClick={() => setOpenCompany((p) => !p)}
              className="min-w-[150px] h-10 px-4 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 cursor-pointer flex justify-between items-center gap-2"
            >
              <span>Company {companyFilter.length > 0 ? `(${companyFilter.length})` : ""}</span>
              <FiChevronDown className="text-gray-400" />
            </button>

            {openCompany && (
              <DropdownMulti
                title="Companies"
                list={companyList}
                selected={companyFilter}
                onToggle={(val) => toggleMulti(val, setCompanyFilter)}
                onSelectAll={() => selectAll(companyList, setCompanyFilter)}
                onClear={() => clearAll(setCompanyFilter)}
              />
            )}
          </div>

          {/* HR */}
          <div className="relative" ref={hrRef}>
            <button
              onClick={() => setOpenHr((p) => !p)}
              className="min-w-[150px] h-10 px-4 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 cursor-pointer flex justify-between items-center gap-2"
            >
              <span>HR {hrFilter.length ? `(${hrFilter.length})` : ""}</span>
              <FiChevronDown className="text-gray-400" />
            </button>

            {openHr && (
              <DropdownMulti
                title="HRs"
                list={hrList}
                selected={hrFilter}
                onToggle={(val) => toggleMulti(val, setHrFilter)}
                onSelectAll={() => selectAll(hrList, setHrFilter)}
                onClear={() => clearAll(setHrFilter)}
              />
            )}
          </div>

          {/* POSITION */}
          <div className="relative" ref={positionRef}>
            <button
              onClick={() => setOpenPosition((p) => !p)}
              className="min-w-[150px] h-10 px-4 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 cursor-pointer flex justify-between items-center gap-2"
            >
              <span>Position {positionFilter.length ? `(${positionFilter.length})` : ""}</span>
              <FiChevronDown className="text-gray-400" />
            </button>

            {openPosition && (
              <DropdownMulti
                title="Positions"
                list={positionList}
                selected={positionFilter}
                onToggle={(val) => toggleMulti(val, setPositionFilter)}
                onSelectAll={() => selectAll(positionList, setPositionFilter)}
                onClear={() => clearAll(setPositionFilter)}
              />
            )}
          </div>

          {/* ASSOCIATE */}
          <div className="relative" ref={associateRef}>
            <button
              onClick={() => setOpenAssociate((p) => !p)}
              className="min-w-[150px] h-10 px-4 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 cursor-pointer flex justify-between items-center gap-2"
            >
              <span>Associate {associateFilter.length ? `(${associateFilter.length})` : ""}</span>
              <FiChevronDown className="text-gray-400" />
            </button>

            {openAssociate && (
              <DropdownMulti
                title="Associates"
                list={associateList}
                selected={associateFilter}
                onToggle={(val) => toggleMulti(val, setAssociateFilter)}
                onSelectAll={() => selectAll(associateList, setAssociateFilter)}
                onClear={() => clearAll(setAssociateFilter)}
              />
            )}
          </div>

          {/* STATUS */}
          <div className="relative">
            <select
              className="min-w-[150px] h-10 px-4 pr-8 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 cursor-pointer appearance-none"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              {statusList.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none w-3 h-3" />
          </div>

          {/* START DATE */}
          <label className="flex items-center gap-2 text-xs font-semibold text-gray-600">
            From:
            <input
              type="date"
              className="h-10 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </label>

          {/* END DATE */}
          <label className="flex items-center gap-2 text-xs font-semibold text-gray-600">
            To:
            <input
              type="date"
              className="h-10 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </label>

          {/* RESET */}
          <button
            onClick={resetFilters}
            className="h-10 px-4 border border-gray-200 text-xs font-bold text-gray-655 hover:bg-gray-50 rounded-xl transition duration-200 active:scale-[0.99] flex items-center gap-2"
          >
            <FiFilter className="text-gray-400" /> Reset Filters
          </button>
        </div>
      </div>

      {/* ======================================================================
                          TABLE
      ====================================================================== */}

        {/* TABLE */}
<div className="relative z-0 overflow-x-auto">
            <table className="min-w-full text-xs">
            <thead>
              <tr className="border-b border-gray-150 bg-gray-50/50 text-[10px] font-bold text-gray-500 uppercase tracking-wider text-left sticky top-0 z-10">
                <th className="px-4 py-3.5 w-12">
                  <input
                    type="checkbox"
                    checked={
                      selected.length === filteredData.length &&
                      filteredData.length > 0
                    }
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                </th>
                <th className="px-4 py-3.5 w-12">Sr</th>
                <th className="px-4 py-3.5 min-w-[120px]">Received On</th>

                {columns.company_name && (
                  <th className="px-4 py-3.5 min-w-[150px]">Company Name</th>
                )}
                {columns.hr_name && (
                  <th className="px-4 py-3.5 min-w-[120px]">HR Name</th>
                )}
                {columns.position && (
                  <th className="px-4 py-3.5 min-w-[150px]">Position</th>
                )}
                {columns.no_of_openings && (
                  <th className="px-4 py-3.5 min-w-[100px] text-center">
                    No. of Openings
                  </th>
                )}
                {columns.location && (
                  <th className="px-4 py-3.5 min-w-[120px]">Location</th>
                )}

              {columns.budget && (
                <th className="p-3 text-right min-w-[100px] text-gray-700">Budget CTC</th>
              )}
              {columns.percentage && (
                <th className="p-3 text-center min-w-[90px] text-gray-700">Vendor %</th>
              )}
              {columns.company_revenue && (
                <th className="p-3 text-right min-w-[120px] text-gray-700">
                  Company Revenue
                </th>
              )}

              {columns.associate_name && (
                <th className="p-3 text-left min-w-[120px] text-gray-700">Associate Name</th>
              )}

              {/* PIPELINE HEADERS */}
              {columns.profiles_shared && (
                <th className="p-3 text-center min-w-[100px] text-gray-700">
                  Profiles Shared
                </th>
              )}
              {columns.feedback_pending && (
                <th className="p-3 text-center min-w-[100px] text-gray-700">
                  Feedback Pending
                </th>
              )}
              {columns.screening && (
                <th className="p-3 text-center min-w-[80px] text-gray-700">Screening</th>
              )}
              {columns.shortlisted && (
                <th className="p-3 text-center min-w-[90px] text-gray-700">Shortlisted</th>
              )}
              {columns.interview_l1 && (
                <th className="p-3 text-center min-w-[100px] text-gray-700">Interview L1</th>
              )}
              {columns.interview_l2 && (
                <th className="p-3 text-center min-w-[100px] text-gray-700">Interview L2</th>
              )}
              {columns.face_to_face && (
                <th className="p-3 text-center min-w-[100px] text-gray-700">
                  Face to Face
                </th>
              )}
              {columns.selected && (
                <th className="p-3 text-center min-w-[80px] text-gray-700">Selected</th>
              )}
              {columns.offered && (
                <th className="p-3 text-center min-w-[80px] text-gray-700">Offered</th>
              )}
              {columns.joining_pending && (
                <th className="p-3 text-center min-w-[100px] text-gray-700">
                  Joining Pending
                </th>
              )}
              {columns.joined && (
                <th className="p-3 text-center min-w-[80px] text-gray-700">Joined</th>
              )}
              {columns.rejected && (
                <th className="p-3 text-center min-w-[80px] text-gray-700">Rejected</th>
              )}
              {columns.no_show && (
                <th className="p-3 text-center min-w-[80px] text-gray-700">No Show</th>
              )}
              {columns.hold && (
                <th className="p-3 text-center min-w-[80px] text-gray-700">Hold</th>
              )}
              {columns.backout && (
                <th className="p-3 text-center min-w-[80px] text-gray-700">Backout</th>
              )}
              {columns.duplicate && (
                <th className="p-3 text-center min-w-[80px] text-gray-700">Duplicate</th>
              )}

              {columns.final_status && (
                <th className="p-3 text-left min-w-[100px] text-gray-700">Final Status</th>
              )}
            </tr>
          </thead>

          <tbody>
            {filteredData.length > 0 ? (
              filteredData.map((r, i) => (
                <tr key={r.job_id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors text-gray-800">
                  <td className="p-3 text-center">
                    <input
                      type="checkbox"
                      checked={selected.includes(r.job_id)}
                      onChange={() => toggleSelect(r.job_id)}
                      className="form-checkbox h-4 w-4 text-blue-600 rounded"
                    />
                  </td>

                  <td className="p-3">{i + 1}</td>
                  <td className="p-3">
                    {r.position_received_on
                      ? dayjs(r.position_received_on).format("DD-MM-YYYY")
                      : "-"}
                  </td>

                  {columns.company_name && (
                    <td className="p-3">{r.company_name || "-"}</td>
                  )}
                  {columns.hr_name && (
                    <td className="p-3">{r.hr_name || "-"}</td>
                  )}
                  {columns.position && (
                    <td className="p-3">{r.position || "-"}</td>
                  )}
                  {columns.no_of_openings && (
                    <td className="p-3 text-center">{r.no_of_openings ?? 0}</td>
                  )}
                  {columns.location && (
                    <td className="p-3">{r.location || "-"}</td>
                  )}

                  {columns.budget && (
                    <td className="p-3 text-right">
                      {r.budget ? Number(r.budget).toLocaleString("en-IN") : "0"}
                    </td>
                  )}

                  {columns.percentage && (
                    <td className="p-3 text-center">
                      {r.percentage ? `${r.percentage}%` : "-"}
                    </td>
                  )}

                  {columns.company_revenue && (
                    <td className="p-3 text-right font-semibold text-green-700">
                      {r.company_revenue
                        ? Number(r.company_revenue).toLocaleString("en-IN")
                        : "0"}
                    </td>
                  )}

                  {columns.associate_name && (
                    <td className="p-3">{r.associate_name || "-"}</td>
                  )}

                  {columns.profiles_shared && (
                    <td className="p-3 text-center">{r.profiles_shared ?? 0}</td>
                  )}
                  {columns.feedback_pending && (
                    <td className="p-3 text-center">{r.feedback_pending ?? 0}</td>
                  )}
                  {columns.screening && (
                    <td className="p-3 text-center">{r.screening ?? 0}</td>
                  )}
                  {columns.shortlisted && (
                    <td className="p-3 text-center">{r.shortlisted ?? 0}</td>
                  )}
                  {columns.interview_l1 && (
                    <td className="p-3 text-center">{r.interview_l1 ?? 0}</td>
                  )}
                  {columns.interview_l2 && (
                    <td className="p-3 text-center">{r.interview_l2 ?? 0}</td>
                  )}
                  {columns.face_to_face && (
                    <td className="p-3 text-center">{r.face_to_face ?? 0}</td>
                  )}
                  {columns.selected && (
                    <td className="p-3 text-center text-blue-600">
                      {r.selected ?? 0}
                    </td>
                  )}
                  {columns.offered && (
                    <td className="p-3 text-center">{r.offered ?? 0}</td>
                  )}
                  {columns.joining_pending && (
                    <td className="p-3 text-center">{r.joining_pending ?? 0}</td>
                  )}
                  {columns.joined && (
                    <td className="p-3 text-center font-semibold text-green-600">
                      {r.joined ?? 0}
                    </td>
                  )}
                  {columns.rejected && (
                    <td className="p-3 text-center text-red-500">
                      {r.rejected ?? 0}
                    </td>
                  )}
                  {columns.no_show && (
                    <td className="p-3 text-center">{r.no_show ?? 0}</td>
                  )}
                  {columns.hold && (
                    <td className="p-3 text-center">{r.hold ?? 0}</td>
                  )}
                  {columns.backout && (
                    <td className="p-3 text-center">{r.backout ?? 0}</td>
                  )}
                  {columns.duplicate && (
                    <td className="p-3 text-center">{r.duplicate ?? 0}</td>
                  )}

                  {columns.final_status && (
                    <td className="p-3">
                      <span
                        className={`${statusBadge(
                          r.final_status
                        )} px-2.5 py-1 rounded-full text-xs font-medium`}
                      >
                        {r.final_status || "-"}
                      </span>
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={Object.values(columns).filter(Boolean).length + 3} // dynamically calculate colspan
                  className="text-center p-6 text-gray-500"
                >
                  No matching records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ======================================================================
    🔽 Reusable Multi-Select Dropdown Component
====================================================================== */

function DropdownMulti({
  title,
  list,
  selected,
  onToggle,
  onSelectAll,
  onClear,
}) {
  return (
    <div className="absolute left-0 mt-1.5 bg-white border border-gray-100 rounded-2xl shadow-xl z-50 w-56 max-h-64 overflow-y-auto p-3.5 animate-fadeIn">
      <div className="flex justify-between items-center mb-2 pb-2 border-b border-gray-100">
        <strong className="text-xs font-bold text-gray-700 uppercase tracking-wider">{title}</strong>
        <div className="flex gap-2">
          <button
            className="text-[10px] text-indigo-650 hover:text-indigo-800 transition font-bold"
            onClick={onSelectAll}
          >
            All
          </button>
          <button
            className="text-[10px] text-gray-500 hover:text-gray-750 transition font-bold"
            onClick={onClear}
          >
            Clear
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-1">
        {list.length > 0 ? (
          list.map((item) => (
            <label key={item} className="flex items-center gap-2 text-xs py-1.5 cursor-pointer hover:bg-gray-50 rounded items-center text-gray-750 font-semibold">
              <input
                type="checkbox"
                checked={selected.includes(item)}
                onChange={() => onToggle(item)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="truncate">{item}</span>
            </label>
          ))
        ) : (
          <p className="text-[11px] text-gray-400 p-2 text-center">No options available</p>
        )}
      </div>
    </div>
  );
}